import {AxiosRequestConfig} from 'axios';
import dayjs from 'utils/dayjs';
import {spotheroPost} from 'api';
import {apiDateFormat} from 'utils/date-time';
import {sendSentryException} from 'utils/error-handling';
import {camelizeKeys} from 'utils/format-strings';
import isSpotheroAdmin from 'utils/is-spothero-admin';
import {ParsedPaginationMeta, parsePaginationMeta} from 'utils/api';
import {ApiResponse} from 'api/api.types';
import {Prettify} from 'types/helpers.types';

import {Blackout, BlackoutResponse} from './blackouts.types';

export const ORDER = {
    asc: 'asc' as const,
    desc: 'desc' as const,
};

export type OrderType = (typeof ORDER)[keyof typeof ORDER];

export const SORT_BY = {
    starts: 'starts' as const,
    ends: 'ends' as const,
};

export type SortByType = (typeof SORT_BY)[keyof typeof SORT_BY];

export const BLACKOUT_STATUS = {
    all: 'all',
    deleted: 'deleted',
    disabled: 'disabled',
    upcoming: 'upcoming',
    active: 'active',
    expired: 'expired',
};

export type BlackoutStatusType =
    (typeof BLACKOUT_STATUS)[keyof typeof BLACKOUT_STATUS];

interface GetBlackoutsParams {
    facilityIDs: string | number;
    blackoutStatus?: BlackoutStatusType;
    dateFrom?: string;
    dateTo?: string | null;
    page?: number;
    perPage?: number;
    sortBy?: SortByType;
    order?: OrderType;
}

interface FormattedBlackoutsResponse {
    data: Blackout[];
    meta: ParsedPaginationMeta;
}

export const formatGetBlackoutsResponse = (
    response: ApiResponse<BlackoutResponse[]>
) => {
    const formattedData: Blackout[] = camelizeKeys(
        response.data.data.results || []
    );

    const formattedResults = formattedData.map((blackout) => ({
        ...blackout,
        starts: blackout.starts || null,
        ends: blackout.ends || null,
        createdBy: blackout.createdBy
            ? isSpotheroAdmin(blackout.createdBy)
                ? 'SpotHero Admin'
                : blackout.createdBy
            : 'N/A',
    }));

    const formattedMeta = parsePaginationMeta(response.data.meta);

    const formattedResponse: Prettify<FormattedBlackoutsResponse> = {
        data: formattedResults,
        meta: formattedMeta,
    };

    return formattedResponse;
};

/**
 * Gets a facility's blackouts
 * Note that this endpoint is undocumented and is actually a POST request not a GET request.
 *
 * @param facilityIDs - stringfied list of ids or single id of the facility/facilities to get blackouts for
 * @param blackoutStatus - The status of the blackouts to get
 * @param dateFrom - Optional date to get blackouts from in YYYY-MM-DD format
 * @param dateTo - Optional date to get blackouts to in YYYY-MM-DD format
 * @param page - The page of results to get
 * @param perPage - The number of results per page to get
 * @param sortBy - The field to sort by
 * @param order - The order to sort by ascending or descending
 * @param config - The config to send to the endpoint
 *
 * @see '/api/v1/facilities/blackouts/'
 */
const getBlackouts = async (
    {
        facilityIDs,
        blackoutStatus = BLACKOUT_STATUS.all,
        dateFrom = dayjs().format(apiDateFormat),
        dateTo = null,
        page = 1,
        perPage = 25,
        sortBy = SORT_BY.starts,
        order = ORDER.desc,
    }: GetBlackoutsParams,
    config: AxiosRequestConfig = {}
) => {
    try {
        const args = {
            page,
            dateTo,
            dateFrom,
            order,
            blackoutStatus,
            sortBy,
            perPage,
        };

        const response = await spotheroPost<BlackoutResponse[]>(
            'facilities/blackouts/',
            {
                facilityIDs,
            },
            {
                ...config,
                params: args,
            }
        );

        const formattedResponse = formatGetBlackoutsResponse(response);

        return formattedResponse;
    } catch (error) {
        sendSentryException(error);
        throw error;
    }
};

export default getBlackouts;
